// Very helpful stuff on this page:
// https://docs.aws.amazon.com/cognito/latest/developerguide/token-endpoint.html

import axios, {Axios, AxiosRequestConfig} from 'axios'

import {warning} from 'toastr'
import CognitoState from './core/_state'
import CognitoTokens from './core/_tokens'
import CognitoUrls from './core/_urls'
import CognitoUser from './core/_user'

export function retrieveIscoutDestination() {
  return CognitoUrls.retrieveIscoutDestination()
}

// Set up a separate copy of Axios so that the interceptors do not fire.
const CognitoAxios = axios.create()

async function tradeCodeForTokens(code: string) {
  const response = await CognitoAxios.post(
    CognitoUrls.codeForToken(code),
    {},
    {
      headers: {
        'content-type': 'application/x-www-form-urlencoded',
      },
    }
  )
  //
  const stop = 'here for lookng at tokens to use for timers'
  //response.data.expires_in 3600
  //setWarningAlertForUser(response.data.expires_in)
  CognitoTokens.store(response.data)
}

async function setWarningAlertForUser(expiryTime: number) {
  const warningTime = expiryTime - 1000 * 60 * 1 // 1 minute before expiry
  const date = new Date()
  const expiryDate = new Date(date.getTime() + expiryTime)
  const warningDate = new Date(date.getTime() + warningTime)
  const warningMessage = `Your session will expire at ${warningDate.toLocaleTimeString()}. Please refresh your session to continue using the application.`
  setTimeout(() => {
    warningDate.setMinutes(warningDate.getMinutes() + 1)
    if (warningDate >= date) {
      warning(warningMessage, 'Session Expiry Warning', {
        timeOut: 0,
        extendedTimeOut: 0,
        closeButton: true,
        tapToDismiss: false,
        positionClass: 'toast-top-center',
      })
    }
  }, 1000 * 60 * 1) // 55 minutes
}

async function refreshTokens() {
  const response = await CognitoAxios.post(
    CognitoUrls.refresh,
    {},
    {
      headers: {
        'content-type': 'application/x-www-form-urlencoded',
      },
    }
  )
  //
  const stop = 'here for lookng at tokens to use for timers'
  CognitoTokens.store(response.data)
}

async function retrieveUserData() {
  const response = await CognitoAxios.get(CognitoUrls.user, {
    headers: {
      Authorization: `Bearer ${CognitoTokens.access}`,
    },
  })
  CognitoUser.store(response.data)
}

// Authenticates the user and returns their user data.
export async function authenticate() {
  // First, check to see the user has already authenticated (or we're on localhost).
  //detectEnv() === 'localhost' ||
  if (CognitoUser.username && CognitoTokens.refresh) {
    return {
      user: CognitoUser,
      redirect: false,
    }
  }

  // This user has not authenticated, but they might be coming back from a trip to
  // Cognito login. Check to see if the current URL contains a "code" parameter.
  const url = new URL(window.location.href)
  const code = url.searchParams.get('code')

  // No "code" parameter was found. Send them to the login page to get one.
  if (!code) {
    CognitoState.create()
    CognitoUrls.storeIscoutDestination(url.pathname + url.search)
    //this will hit cognito which will send code and state param back in the url and not require login, so how does new user get created?
    const x = CognitoUser
    const l = window.location.replace(CognitoUrls.login)
    return {user: CognitoUser, redirect: true}
  }

  // The user has a code; we just need to finish authenticating them.

  // Check that the "state" param (a CSRF token) matches what we expect. (The
  // validate() method will throw an exception if validation fails.)
  CognitoState.validate(url.searchParams.get('state'))

  // Send the code to Cognito, which will respond with some tokens. These get
  // stored in CognitoTokens.
  await tradeCodeForTokens(code)

  // Send the tokens to Cognito, which will respond with user data. This gets
  // stored in CognitoUser.
  await retrieveUserData()

  // Success!
  return {
    user: CognitoUser,
    redirect: false,
  }
}

export function logout() {
  console.log('Logging out user.')
  localStorage.clear()
  CognitoUser.logout()
  CognitoTokens.logout()
  window.location.replace(CognitoUrls.logout)
}

// Sign all Axios calls by default, so we don't have to think about it.
export function setupAxios(axiosInstance: Axios) {
  axiosInstance.defaults.headers.common.Accept = 'application/json'
  axiosInstance.interceptors.request.use(
    async (config: AxiosRequestConfig): Promise<AxiosRequestConfig> => {
      // if (detectEnv() === 'localhost') {
      //   return config
      // }

      //if you don't call this, then config.headers.authorization doesn't get set
      await authenticate()
      if (!CognitoTokens.id) {
        await refreshTokens()
      }
      if (config.headers === undefined) {
        throw new Error("Don't think this can actually happen, but Axios is not TypeScript-aware.")
      }
      config.headers.Authorization = `${CognitoTokens.id}`
      return config
    },
    (err: any) => Promise.reject(err)
  )
}
